import React, { Fragment, useEffect, useState } from 'react'
import { LoadingSpinner } from '../components'
import { API_BASE_URL } from '../data'
import {
    VolunteerOverviewMetaProps,
    Area,
    Shift,
    Event,
    GroupedDataType
} from '../data/types'

type Props = {
    meta: VolunteerOverviewMetaProps,
    event?: Event,
    area?: Area,
    shift?: Shift
}

const ETHNICITY_COLUMNS = [
    'East Africa',
    'Indian Subcontinent',
    'Afghanistan',
    'Tajikistan',
    'Syria',
    'Iran',
    'Mixed Origin',
    'Other',
    'Blank'
] as const;

export type TableRowData = {
    id: number;
    name: string;
    total: number;
    gender: { m: number; f: number; blank: number };
    location: { london: number; region: number };
    iff: number
    ethnicity: Record<string, number>;
};

export const calcPct = (part: number | undefined | null, total: number) => {
    if (!total || total === 0) return 0;
    const value = part || 0;
    return Math.round((value / total) * 100);
};

export const processStatsData = (name: string, id: number, data: GroupedDataType): TableRowData => {
    const genderList = data['gender'] || [];
    const regionList = data['region'] || [];
    const originList = data['family_origin'] || [];
    const iffList = data['multi_faith_family'] || [];

    const genderTotal = genderList.reduce((sum, item) => sum + item.count, 0);

    const m = genderList.find(g => g.name === 'Male')?.count || 0;
    const f = genderList.find(g => g.name === 'Female')?.count || 0;
    const genderBlank = genderList
        .filter(g => !['Male', 'Female'].includes(g.name))
        .reduce((sum, item) => sum + item.count, 0);

    let london = 0;
    let region = 0;
    regionList.forEach(regionItem => {
        if (regionItem.name && regionItem.name.toLowerCase().includes('london')) {
            london += regionItem.count;
        } else {
            region += regionItem.count;
        }
    });

    const iffCount = iffList.find(item => String(item.name) === '1' || String(item.name).toLowerCase() === 'true')?.count || 0

    const ethnicityCounts: Record<string, number> = {};
    ETHNICITY_COLUMNS.forEach(col => ethnicityCounts[col] = 0);

    originList.forEach(origin => {
        const rawName = origin.name;
        if (!rawName || rawName.trim() === '' || rawName === 'N/A') {
            ethnicityCounts['Blank'] += origin.count;
            return;
        }
        const name = rawName.trim();
        const isKnownColumn = ETHNICITY_COLUMNS.some(col => col === name);

        if (isKnownColumn) {
            ethnicityCounts[name] += origin.count;
        } else {
            ethnicityCounts['Other'] += origin.count;
        }
    });

    return {
        id,
        name,
        total: genderTotal,
        gender: { m, f, blank: genderBlank },
        location: { london, region },
        iff: iffCount,
        ethnicity: ethnicityCounts
    };
};

export const VolunteerStatsTable = ({ meta, event, area, shift }: Props) => {

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [tableRows, setTableRows] = useState<TableRowData[]>([])

    const mode = shift ? 'shift' : (area ? 'shift' : (event ? 'area' : 'event'));

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event?.id, area?.id, shift?.id, mode]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        setTableRows([]);

        try {
            let entities: any[] = [];
            let url = '';

            if (mode === 'event') {
                url = `${API_BASE_URL}/event`;
            } else if (mode === 'area' && event) {
                url = `${API_BASE_URL}/area/${event.id}`;
            } else if (mode === 'shift' && area) {
                url = `${API_BASE_URL}/shift/${area.id}`;
            }

            if (url) {
                const res = await fetch(url, { credentials: 'include' });
                if (!res.ok) throw new Error('Failed to fetch data list');

                const rawData = await res.json();
                let dataToProcess = Array.isArray(rawData) ? rawData : (rawData.data || []);

                if (dataToProcess.length > 0 && Array.isArray(dataToProcess[0])) {
                    entities = dataToProcess.flat();
                } else {
                    entities = dataToProcess;
                }
            }

            // Filter out invalid entities
            const validEntities = entities.filter(e => e && e.id);

            if (validEntities.length === 0) {
                setTableRows([]);
                return;
            }

            // Use batch endpoint instead of N separate API calls
            const entityIds = validEntities.map(e => e.id).join(',');
            const fields = ['age_band', 'gender', 'family_origin', 'language', 'jamat_khana', 'region', 'multi_faith_family'];
            const fieldsParam = fields.map(f => `fields[]=${f}`).join('&');

            const batchRes = await fetch(
                `${API_BASE_URL}/dashboard/stats/volunteer/batch?entity_type=${mode}&entity_ids=${entityIds}&${fieldsParam}`,
                { credentials: 'include' }
            );

            if (!batchRes.ok) {
                throw new Error('Failed to fetch batch statistics');
            }

            const batchStats: Record<string, GroupedDataType> = await batchRes.json();

            // Process the batch results
            const rows: TableRowData[] = validEntities
                .map(entity => {
                    const stats = batchStats[entity.id];
                    if (!stats) return null;

                    // Determine Display Name
                    let displayName = entity.name;
                    if (mode === 'shift') {
                        displayName = entity.title || entity.duty?.name || entity.name || `Shift ${entity.id}`;
                    }

                    return processStatsData(displayName, entity.id, stats);
                })
                .filter((r): r is TableRowData => r !== null);

            setTableRows(rows);

        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : "Error fetching data");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Fragment>
            <div className="flex-1 w-full max-w-full">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{meta.title}</h1>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">{meta.description}</p>
                </div>

                <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-3 flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center">
                        <span className="font-semibold text-blue-800 dark:text-blue-300 me-2">Scope:</span>
                        <span className="bg-white dark:bg-gray-800 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300">
                            {mode === 'event' ? 'All Events' : (mode === 'area' ? 'Areas' : 'Shifts')}
                        </span>
                    </div>

                    {event && (
                        <div className="flex items-center">
                            <span className="font-semibold text-gray-500 dark:text-gray-400 me-2">Event:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{event.name}</span>
                        </div>
                    )}

                    {area && (
                        <div className="flex items-center">
                            <span className="text-gray-400 mx-2">/</span>
                            <span className="font-semibold text-gray-500 dark:text-gray-400 me-2">Area:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{area.name}</span>
                        </div>
                    )}
                </div>

                {loading && <LoadingSpinner meta={{ loadingText: 'Calculating statistics...' }} />}

                {error && (
                    <div className="bg-red-50 text-red-800 p-4 rounded-lg mb-6 border border-red-200">
                        {error}
                    </div>
                )}

                {!loading && !error && (
                    <div className="w-full bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-center text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-start sticky start-0 bg-gray-50 dark:bg-gray-700 z-20 border-b border-gray-200 dark:border-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                                            {mode === 'event' ? 'Event' : (mode === 'area' ? 'Area' : 'Shift')}
                                        </th>
                                        <th className="px-2 py-3 border-s border-b border-gray-200 dark:border-gray-600">Total</th>
                                        <th colSpan={3} className="px-2 py-3 border-s border-b border-gray-200 dark:border-gray-600">Gender</th>
                                        <th colSpan={2} className="px-2 py-3 border-s border-b border-gray-200 dark:border-gray-600">Location (JK)</th>
                                        <th className="px-2 py-3 border-s border-b border-gray-200 dark:border-gray-600">IFF</th>
                                        <th colSpan={ETHNICITY_COLUMNS.length} className="px-2 py-3 border-s border-b border-gray-200 dark:border-gray-600">Ethnic Origin</th>
                                    </tr>
                                    <tr className="border-b border-gray-200 dark:border-gray-600 text-[10px]">
                                        <th className="sticky start-0 bg-gray-50 dark:bg-gray-700 z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"></th>
                                        <th className="border-s border-gray-200 dark:border-gray-600"></th>

                                        <th className="px-2 py-2 border-s border-gray-200 dark:border-gray-600">M (%)</th>
                                        <th className="px-2 py-2">F (%)</th>
                                        <th className="px-2 py-2">Blank</th>

                                        <th className="px-2 py-2 border-s border-gray-200 dark:border-gray-600">Ldn (%)</th>
                                        <th className="px-2 py-2">Reg (%)</th>
                                        <th className="px-2 py-2 border-s border-gray-200 dark:border-gray-600">%</th>

                                        {ETHNICITY_COLUMNS.map((col, index) => (
                                            <th key={col} className={`px-2 py-2 ${index === 0 ? 'border-s border-gray-200 dark:border-gray-600' : ''}`}>
                                                {col === 'Indian Subcontinent' ? 'Ind/Sub (%)' : `${col} (%)`}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableRows.map((row) => (
                                        <tr key={row.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                            <th scope="row" className="px-4 py-3 font-medium text-gray-900 dark:text-white text-start sticky start-0 bg-white dark:bg-gray-800 z-10 border-e border-gray-200 dark:border-gray-700 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                                                {row.name}
                                            </th>
                                            <td className="px-2 py-3 font-bold text-gray-900 dark:text-white border-s border-gray-200 dark:border-gray-600">
                                                {row.total}
                                            </td>

                                            <td className="px-2 py-3 border-s border-gray-200 dark:border-gray-600">{calcPct(row.gender.m, row.total)}%</td>
                                            <td className="px-2 py-3">{calcPct(row.gender.f, row.total)}%</td>
                                            <td className="px-2 py-3 text-gray-400">{calcPct(row.gender.blank, row.total) > 0 ? `${calcPct(row.gender.blank, row.total)}%` : '-'}</td>


                                            <td className={`px-2 py-3 border-s border-gray-200 dark:border-gray-600`}>
                                                {calcPct(row.location.london, row.total)}%
                                            </td>
                                            <td className="px-2 py-3">{calcPct(row.location.region, row.total)}%</td>
                                            <td className="px-2 py-3 border-s border-gray-200 dark:border-gray-600">
                                                {calcPct(row.iff, row.total)}%
                                            </td>

                                            {ETHNICITY_COLUMNS.map((col, index) => (
                                                <td key={col} className={`px-2 py-3 ${index === 0 ? 'border-s border-gray-200 dark:border-gray-600' : ''}`}>
                                                    {calcPct(row.ethnicity[col], row.total)}%
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    {tableRows.length === 0 && (
                                        <tr><td colSpan={100} className="px-6 py-8 text-center text-gray-500">No data found.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </Fragment>
    )
}
